import React from 'react';

/**
 * Car Spotlight Component for showing detailed view of a car
 * @param {Object} props - Component props
 * @param {Object} props.car - Car data
 * @param {Function} props.onClose - Close handler
 */
const CarSpotlight = ({ car, onClose }) => {
  if (!car) return null;
  
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-2xl w-full max-w-3xl mx-auto">
      {/* Header with close button */}
      <div className="relative bg-gradient-to-r from-blue-900 to-black">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-black/50 text-white p-1.5 rounded-full hover:bg-black/80 transition-colors"
          aria-label="Close details"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        {/* Car image with overlay */}
        <div className="relative">
          <img 
            src={car.image} 
            alt={`BMW Art Car by ${car.artist}`}
            className="w-full h-80 object-cover opacity-90"
            onError={(e) => {
              e.target.src = "/api/placeholder/800/450";
              e.target.alt = "Image not available";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          
          {/* Car title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="inline-block px-3 py-1 bg-blue-700 rounded-full text-sm mb-2">
              Car #{car.number} • {car.year}
            </div>
            <h3 className="text-3xl font-medium">{car.artist}</h3>
            <p className="text-xl text-gray-300">{car.model}</p>
          </div>
        </div>
      </div>
      
      {/* Car details */}
      <div className="p-6 bg-white">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
              {car.year}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-gray-700">
              BMW Art Car Collection
            </span>
          </div>
          <div className="flex items-center text-sm bg-gray-100 px-3 py-1 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            50th Anniversary
          </div>
        </div>
        
        <div className="prose max-w-none">
          <h4 className="text-xl font-medium text-gray-900">About this Art Car</h4>
          <p className="text-gray-700">
            This BMW Art Car was created by {car.artist} in {car.year} using a {car.model} as the canvas. 
            It is part of the BMW Art Car Collection, which began in 1975 when Alexander Calder painted a BMW 3.0 CSL.
          </p>
          
          <h4 className="text-xl font-medium text-gray-900 mt-6">Artist</h4>
          <p className="text-gray-700">
            {car.artist} is renowned for their unique artistic style and vision. 
            This collaboration with BMW represents a fusion of automotive design and fine art.
          </p>
        </div>
        
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              BMW Art Car #{car.number}
            </div>
            <div>
              <button 
                className="inline-flex items-center text-blue-700 hover:text-blue-900"
                onClick={() => {
                  // This would link to more information in a real app
                }}
              >
                <span>View on BMW.com</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarSpotlight;